import { Play } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { LearningVimeoVideo } from "../types/learning-presentation.js";
import { VimeoVideo } from "./VimeoVideo.js";

export function LearningVideoStill({
  resourceId,
  imageSrc,
  vimeoVideos,
  title,
  compact = false,
}: {
  resourceId: string;
  imageSrc?: string;
  vimeoVideos?: readonly LearningVimeoVideo[];
  title: string;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [showPreviewNote, setShowPreviewNote] = useState(false);

  if (vimeoVideos?.length) {
    const videos = compact ? vimeoVideos.slice(0, 1) : vimeoVideos;
    return (
      <div className={compact ? undefined : "grid max-w-3xl gap-4 sm:grid-cols-2"}>
        {videos.map((video, index) => {
          const videoTitle =
            vimeoVideos.length > 1
              ? t("passportVig.lesson.videoPart", {
                  title,
                  current: index + 1,
                  total: vimeoVideos.length,
                })
              : title;
          return (
            <VimeoVideo
              compact={compact}
              controlId={`passport.learning.${resourceId}.video.${video.videoId}`}
              endSeconds={video.endSeconds}
              key={video.videoId}
              startSeconds={video.startSeconds}
              thumbnailSrc={video.thumbnailSrc}
              title={videoTitle}
              videoId={video.videoId}
            />
          );
        })}
      </div>
    );
  }

  if (!imageSrc) return null;

  return (
    <div>
      <div className="relative aspect-video overflow-hidden rounded-xl bg-muted">
        <img
          alt={compact ? "" : t("passportVig.lesson.imageAlt", { title })}
          className="size-full object-cover"
          height={941}
          loading="lazy"
          src={imageSrc}
          width={1672}
        />
        {compact ? (
          <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
            <span className="flex size-9 items-center justify-center rounded-full bg-foreground/80 text-background shadow-sm">
              <Play className="ml-0.5 size-4 fill-current" />
            </span>
          </div>
        ) : (
          <button
            aria-describedby={showPreviewNote ? `video-note-${resourceId}` : undefined}
            className="group absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-xl bg-foreground/10 transition-colors hover:bg-foreground/20 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-ring"
            data-control-id={`passport.learning.${resourceId}.play-preview`}
            onClick={() => setShowPreviewNote(true)}
            type="button"
          >
            <span className="flex size-16 items-center justify-center rounded-full bg-background/95 text-foreground shadow-md transition-transform group-hover:scale-105">
              <Play aria-hidden="true" className="ml-1 size-7 fill-current" />
            </span>
            <span className="rounded-full bg-foreground/85 px-3 py-1.5 text-xs font-medium text-background">
              {t("passportVig.lesson.play")}
            </span>
          </button>
        )}
        {!compact ? (
          <span className="absolute left-3 top-3 rounded-full bg-background/95 px-3 py-1.5 text-xs font-medium text-foreground">
            {t("passportVig.lesson.videoPreview")}
          </span>
        ) : null}
      </div>
      {showPreviewNote ? (
        <p
          className="mt-2 rounded-lg bg-info px-3 py-2 text-sm text-info-foreground"
          id={`video-note-${resourceId}`}
          role="status"
        >
          {t("passportVig.lesson.previewNote")}
        </p>
      ) : null}
    </div>
  );
}
